import {
  type BaseQueryFn,
  type FetchArgs,
  type FetchBaseQueryError,
  createApi,
  fetchBaseQuery,
} from '@reduxjs/toolkit/query/react';

import * as queryString from 'query-string';

import SnackbarUtils from 'components/SnackbarUtilsConfigurator';
import convertSnakeToTitleCase from 'utils/convertSnakeToTitle';
import { ErrorResponse } from './types';

const baseQuery = fetchBaseQuery({
  baseUrl: import.meta.env.VITE_API_BASE_URL,
  paramsSerializer: (params) => queryString.stringify(params),
  credentials: 'include',
});

const baseQueryWithReauth: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError
> = async (args, api, extraOptions) => {
  const result = await baseQuery(args, api, extraOptions);
  if (!result.error) return result;

  const currentUrl = window.location.href;

  if (
    result.error.status === 401 &&
    !currentUrl.includes('/login') &&
    !currentUrl.includes('/activate-account')
  ) {
    window.location.href = '/login';
  }

  const errorResponse = result.error.data;

  // Enqueue snackbar errors based on the standardised error response format
  if (
    // Checking if the error response is of the correct ErrorResponse type and structure
    errorResponse &&
    (errorResponse as ErrorResponse).error_type &&
    (errorResponse as ErrorResponse).errors
  ) {
    const typedErrorResponse = errorResponse as ErrorResponse;

    // Only validation errors have multiple errors
    if (typedErrorResponse.error_type === 'validation_error') {
      typedErrorResponse.errors.forEach((error) => {
        SnackbarUtils.error(`Field: '${error.field}' - ${error.message}`);
      });
    } else {
      const code = convertSnakeToTitleCase(typedErrorResponse.errors[0].code);

      SnackbarUtils.error(`${code}: ${typedErrorResponse.errors[0].message}`);
    }
  } else {
    SnackbarUtils.error('Something went wrong');
  }

  return result;
};

export const alertStatsTags = [
  'AlertCounts',
  'AlertCountsByBlock',
  'AlertCountsByDate',
  'AlertCountsRolling',
];

export const tags = [
  'Site',
  'Camera',
  'Alert',
  'AlertBase',
  'CameraBase',
  'CameraSudoSettings',
  'User',
  'AlertComments',
  'Group',
  'AywaProcessingNodes',
  'CurrentUser',
  'FrCalibration',
  'FrPeople',
  'FrAuthorizations',
  'VaCalibration',
  'VaRecords',
  'VaRecordsInfo',
  'LicensePlates',
  'LicensePlatesAuth',
  'Reseller',
  'CompanyUser',
  'Company',
  'ResellerUser',
  'Zone',
  'UserUnreadNotificationsCount',
  'UserNotifications',
  'ApnCameraRelations',
  ...alertStatsTags,
];

const emptySplitApi = createApi({
  baseQuery: baseQueryWithReauth,
  endpoints: () => ({}),
  tagTypes: tags,
});

export default emptySplitApi;
